import { REQUISITION_STATUSES, type RequisitionStatus } from '@eombor/contracts';
import type { BadgeTone } from '@eombor/ui';

/**
 * Holatning RANGI — bitta jadval, butun ilova uchun.
 *
 * Ma'nosi: kulrang — hali harakat yo'q yoki ish tugagan; ko'k — kimningdir
 * navbatida turibdi; yashil urg'u — tasdiqlangan va yo'lda; yashil — mahsulot
 * qo'lga tegdi; qizil — to'xtadi.
 *
 * Bu — o'tish QOIDASI emas, faqat ko'rinish: qaysi tugma bosilishi mumkinligini
 * doim server aytadi (`availableTransitions`).
 */
const STATUS_TONES: Record<RequisitionStatus, BadgeTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  ORG_APPROVED: 'accent',
  WAREHOUSE_ACCEPTED: 'accent',
  ACCOUNTING_PENDING: 'info',
  ACCOUNTING_APPROVED: 'accent',
  READY_FOR_PICKUP: 'accent',
  HANDED_OVER: 'success',
  RECEIPT_CONFIRMED: 'success',
  CLOSED: 'neutral',
  REJECTED: 'danger',
  CANCELLED: 'danger',
};

export const requisitionStatusTone = (status: RequisitionStatus): BadgeTone =>
  STATUS_TONES[status];

/** To'xtash holatlari — ulardan keyin oqim davom etmaydi. */
const STOPPED: readonly RequisitionStatus[] = ['REJECTED', 'CANCELLED'];

export const isStoppedStatus = (status: RequisitionStatus): boolean => STOPPED.includes(status);

/**
 * Oqimning ASOSIY yo'li — timeline dagi kelajak bosqichlar shundan chiziladi.
 *
 * Ro'yxat `REQUISITION_STATUSES` ning O'Z tartibidan olinadi (shartnomada u
 * ataylab oqim tartibida yozilgan) va undan ikki to'xtash holati olib
 * tashlanadi. Bu o'tish jadvalining nusxasi EMAS: bu yerda "yo'l qanday
 * ko'rinadi" degan savolga javob bor, "kim nima qila oladi" degan savolga
 * esa faqat server javob beradi.
 */
export const REQUISITION_MAIN_PATH: readonly RequisitionStatus[] = REQUISITION_STATUSES.filter(
  (status) => !STOPPED.includes(status),
);

/** Asosiy yo'ldagi o'rni; yo'lda bo'lmasa `-1`. */
export const mainPathIndex = (status: RequisitionStatus): number =>
  REQUISITION_MAIN_PATH.indexOf(status);

/**
 * KALENDAR KUNI — soatsiz.
 *
 * Muddat kun aniqligida beriladi (`2026-08-25`) va uni VAQT bilan
 * solishtirish jimgina xato tug'diradi: `new Date('2026-08-25')` yarim
 * tunni bildiradi, ya'ni o'sha kunning ertalabi ham "muddati o'tgan"
 * bo'lib chiqardi. Holbuki bugun so'ralgan mahsulot bugun berilsa ham
 * muddat buzilmagan.
 *
 * Kun MAHALLIY kalendardan olinadi.
 */
const dayNumber = (value: Date): number =>
  Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 86_400_000);

/**
 * SOF SANA — vaqtsiz va mintaqasiz (`2026-08-27`).
 *
 * Ilgari bu naqsh oxiriga bog'lanmagan edi va TO'LIQ ISO belgisining ham
 * boshini yulib olardi. Server esa muddatni aynan shunday qaytaradi:
 * `2026-08-27T00:00:00` Toshkent vaqti UTC da `2026-08-26T19:00:00Z`
 * bo'ladi va satrning boshida `2026-08-26` turadi. Natijada bugun
 * so'ralgan tezkor talabnoma «1 kun o'tdi» deb qizarardi — sana esa
 * yonida to'g'ri, 27.08.2026 bo'lib turardi.
 *
 * Endi vaqt qismi bor satr `Date` orqali o'qiladi va undan MAHALLIY kun
 * olinadi: `2026-08-26T19:00:00Z` mahalliy kalendarda 27-avgust bo'lib,
 * mintaqa surilishi o'z-o'zidan yopiladi.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const dueDayOf = (neededBy: string): number | null => {
  const iso = DATE_ONLY.exec(neededBy);
  if (iso === null) {
    const parsed = new Date(neededBy);
    return Number.isNaN(parsed.getTime()) ? null : dayNumber(parsed);
  }
  const [, year, month, day] = iso as unknown as [string, string, string, string];
  return dayNumber(new Date(Number(year), Number(month) - 1, Number(day)));
};

/**
 * Muddatgacha necha KUN qolgani.
 *
 * Musbat — hali vaqt bor; `0` — aynan bugun; manfiy — shuncha kun o'tib
 * ketgan. `null` — muddat umuman ko'rsatilmagan.
 */
export const daysUntilDue = (neededBy: string | null, now: Date = new Date()): number | null => {
  if (neededBy === null) return null;
  const due = dueDayOf(neededBy);
  return due === null ? null : due - dayNumber(now);
};

/**
 * O'TISHNING NOMI — o'zi qaysi qadamda turganiga bog'liq.
 *
 * `REJECTED` ga o'tish ikki xil ish bo'lishi mumkin. Talabnoma hali
 * ko'rib chiqilayotgan bo'lsa, bu haqiqatan RAD ETISH. Mahsulot esa
 * allaqachon band qilinib, olib ketilishini kutayotgan bo'lsa, hech kim
 * hech narsani rad etmayapti — shunchaki bandlik bo'shatilmoqda, chunki
 * uni hech kim olib ketmadi.
 *
 * Yorliq shu ikki ishni ajratadi: "Rad etish" tugmasi "Olib ketishga
 * tayyor" qatorida turgani skladchini "nimani rad etaman?" degan savolga
 * qoldirardi.
 */
const ACTION_LABEL_OVERRIDES: Partial<Record<string, string>> = {
  'ACCOUNTING_APPROVED>REJECTED': 'requisition.action.releaseHold',
  'READY_FOR_PICKUP>REJECTED': 'requisition.action.releaseHold',
};

export const transitionActionKey = (from: RequisitionStatus, to: RequisitionStatus): string =>
  ACTION_LABEL_OVERRIDES[`${from}>${to}`] ?? `requisition.action.${to}`;

/**
 * OLIB KETISH muhlati — shundan keyin bandlik "unutilgan" deb belgilanadi.
 *
 * Avtomatik BO'SHATILMAYDI va bu ataylab: odam mahsulot uchun yo'lga
 * chiqqan bo'lishi mumkin va tizim uni jimgina boshqasiga berib yuborsa,
 * bu eng yomon xato bo'lardi. Belgi faqat KO'RSATADI — bo'shatish qarorini
 * skladchi qabul qiladi.
 */
export const PICKUP_GRACE_DAYS = 5;

/**
 * Necha kundan beri olib ketilmay turibdi.
 *
 * `null` — talabnoma hali tayyor emas yoki allaqachon berilgan, ya'ni
 * ushlab turgan bandlik ham yo'q.
 */
export const waitingDays = (readyAt: string | null, now: Date = new Date()): number | null => {
  if (readyAt === null) return null;
  const ready = new Date(readyAt);
  if (Number.isNaN(ready.getTime())) return null;
  return dayNumber(now) - dayNumber(ready);
};

/**
 * Bandlik UNUTILGANMI — tayyor bo'lgan, lekin olib ketilmagan.
 *
 * Faqat `READY_FOR_PICKUP` da ma'noga ega: boshqa holatlarda yo bron
 * hali yo'q, yo mahsulot allaqachon berilgan.
 */
export const isPickupStale = (
  status: RequisitionStatus,
  readyAt: string | null,
  now: Date = new Date(),
): boolean => {
  if (status !== 'READY_FOR_PICKUP') return false;
  const days = waitingDays(readyAt, now);
  return days !== null && days >= PICKUP_GRACE_DAYS;
};

/** Muddati o'tganmi — `neededBy` bugundan OLDINGI kun. */
export const isOverdue = (neededBy: string | null, now: Date = new Date()): boolean => {
  const days = daysUntilDue(neededBy, now);
  return days !== null && days < 0;
};

/**
 * Ish TUGAGAN holatlar — muddat endi ma'nosini yo'qotadi.
 *
 * `isStoppedStatus` dan farqi bor va farq muhim: to'xtagan (rad etilgan,
 * bekor qilingan) hujjat asosiy yo'lda umuman turmaydi, tugagani esa
 * yo'lni oxirigacha bosib o'tgan. Ikkovida ham "muddati o'tgan" degan
 * qizil belgi YOLG'ON gapiradi — mahsulot allaqachon berilgan.
 */
const FINISHED: readonly RequisitionStatus[] = [
  ...STOPPED,
  'HANDED_OVER',
  'RECEIPT_CONFIRMED',
  'CLOSED',
];

/** Muddati o'tgan va bu hali AHAMIYATLI: ish tugamagan. */
export const isOverdueNow = (
  status: RequisitionStatus,
  neededBy: string | null,
  now: Date = new Date(),
): boolean => !FINISHED.includes(status) && isOverdue(neededBy, now);

/**
 * MUDDATNING HOLATI — ekranda rang va matn shundan chiqadi.
 *
 * `today` alohida holat va aynan shu narsa yetishmasdi: bugungi sana
 * qizil «Muddati o'tgan» bo'lib chiqardi, holbuki u o'tmagan — u
 * TEZKOR. Qizil "kechikdingiz" deydi, sariq esa "bugun ulgurish kerak"
 * deydi va ikkovi bir xil ish emas.
 */
export type DeadlineState = 'none' | 'done' | 'left' | 'today' | 'overdue';

export const deadlineStateOf = (
  status: RequisitionStatus,
  neededBy: string | null,
  now: Date = new Date(),
): DeadlineState => {
  const days = daysUntilDue(neededBy, now);
  if (days === null) return 'none';
  // Ish tugagan bo'lsa muddat endi hech narsani bildirmaydi.
  if (FINISHED.includes(status)) return 'done';
  if (days < 0) return 'overdue';
  return days === 0 ? 'today' : 'left';
};
